import React, { useMemo } from 'react';
import { TEMPLATE_STYLES } from './resumeModel';

const ResumePreview = ({ resume, templateId = 'classic' }) => {
  const style = TEMPLATE_STYLES[templateId] || TEMPLATE_STYLES.classic;

  const contactItems = useMemo(() =>
    [resume.email, resume.phone, resume.location, resume.linkedin, resume.website].filter(Boolean),
    [resume.email, resume.phone, resume.location, resume.linkedin, resume.website]
  );

  return (
    <div
      className="bg-white shadow-lg mx-auto overflow-hidden"
      style={{ width: '100%', maxWidth: 640, fontFamily: style.bodyFont, fontSize: 11, lineHeight: 1.5 }}
      data-testid="resume-preview"
    >
      {/* Header */}
      <div style={{ backgroundColor: style.headerBg, color: style.headerText, padding: '24px 28px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, fontFamily: style.headerFont }}>
          {resume.name || 'Your Name'}
        </h1>
        <div style={{ fontSize: 10, marginTop: 6, opacity: 0.85, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {contactItems.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div style={{ padding: '20px 28px' }}>
        {resume.sections.map((section) => (
          <div key={section.id} style={{ marginBottom: 16 }}>
            <h2 style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
              color: style.accentColor, borderBottom: `2px solid ${style.accentColor}`,
              paddingBottom: 4, marginBottom: 8,
            }}>
              {section.title}
            </h2>

            {section.type === 'summary' && (
              <p style={{ color: '#4b5563', margin: 0 }}>
                {section.content || 'Your professional summary will appear here...'}
              </p>
            )}

            {section.type === 'experience' && (section.items || []).map((item) => (
              <div key={item.id} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ color: '#0f1115' }}>{item.title || 'Job Title'}</strong>
                  <span style={{ fontSize: 10, color: '#6b7280' }}>
                    {item.startDate}{item.startDate && ' - '}{item.current ? 'Present' : item.endDate}
                  </span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 10, marginBottom: 4 }}>
                  {item.company}{item.location ? ` | ${item.location}` : ''}
                </div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {(item.bullets || []).filter(Boolean).map((b) => (
                    <li key={`${item.id}-${b.slice(0, 20)}`} style={{ color: '#374151', marginBottom: 2 }}>{b}</li>
                  ))}
                </ul>
              </div>
            ))}

            {section.type === 'education' && (section.items || []).map((item) => (
              <div key={item.id} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ color: '#0f1115' }}>{item.degree || 'Degree'}</strong>
                  <span style={{ fontSize: 10, color: '#6b7280' }}>{item.year}</span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 10 }}>{item.school}</div>
                {item.details && <p style={{ color: '#4b5563', margin: '2px 0 0' }}>{item.details}</p>}
              </div>
            ))}

            {section.type === 'skills' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(section.items || []).filter(Boolean).map((skill) => (
                  <span key={`skill-${skill}`} style={{
                    padding: '2px 8px', fontSize: 10, backgroundColor: `${style.accentColor}15`,
                    color: style.accentColor, fontWeight: 600,
                  }}>
                    {skill}
                  </span>
                ))}
              </div>
            )}

            {section.type === 'certifications' && (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {(section.items || []).filter(Boolean).map((cert) => (
                  <li key={`cert-${cert}`} style={{ color: '#374151', marginBottom: 2 }}>{cert}</li>
                ))}
              </ul>
            )}

            {section.type === 'projects' && (section.items || []).map((item) => (
              <div key={item.id} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <strong style={{ color: '#0f1115' }}>{item.name || 'Project Name'}</strong>
                  {item.tech && <span style={{ fontSize: 9, color: '#6b7280' }}>({item.tech})</span>}
                </div>
                {item.description && <p style={{ color: '#4b5563', margin: '2px 0 0' }}>{item.description}</p>}
                {item.url && <a href={item.url} style={{ fontSize: 9, color: style.accentColor }}>{item.url}</a>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResumePreview;
